import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Bitcoin, 
  Copy, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ExternalLink,
  ArrowRight,
  Wallet,
  Timer,
  Wifi,
  WifiOff
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import './PublicPayment.css';

const PublicPayment = () => {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // WebSocket for real-time payment status updates
  useEffect(() => {
    if (!paymentId || !payment?.merchant_id) return;
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}//${wsHost}/ws/payments/${payment.merchant_id}`;
    
    let ws = null;
    let reconnectTimeout = null;
    
    const connectWebSocket = () => {
      try {
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('WebSocket connected for payment updates');
          setWsConnected(true);
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'payment_update' && data.payment_id === paymentId) {
              // Update payment status in real-time
              setPayment(prev => ({
                ...prev,
                payment_status: data.status
              }));
              
              if (data.status === 'completed' || data.status === 'finished' || data.status === 'paid') {
                toast.success('Payment confirmed! Thank you for your purchase.');
              } else if (data.status === 'confirming') {
                toast.info('Payment received, confirming on blockchain...');
              }
            }
          } catch (e) {
            // Handle ping/pong
            if (event.data === 'pong') return;
          }
        };
        
        ws.onclose = () => {
          setWsConnected(false);
          // Reconnect after 5 seconds if payment is still pending
          if (payment?.payment_status === 'pending' || payment?.payment_status === 'waiting') {
            reconnectTimeout = setTimeout(connectWebSocket, 5000);
          }
        };
        
        ws.onerror = () => {
          setWsConnected(false);
        };
        
        // Keep-alive ping
        const pingInterval = setInterval(() => {
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        
        return () => clearInterval(pingInterval);
      } catch (error) {
        console.error('WebSocket error:', error);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (ws) ws.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
    // BACKEND_URL is module-scoped; only re-connect when paymentId or merchant_id changes.
    // eslint-disable-next-line
  }, [paymentId, payment?.merchant_id]);

  useEffect(() => {
    fetchPayment();
    
    // Poll for status updates every 10 seconds (fallback if WebSocket fails)
    const interval = setInterval(fetchPayment, 10000);
    return () => clearInterval(interval);
    // fetchPayment is stable; only re-poll when paymentId changes.
    // eslint-disable-next-line
  }, [paymentId]);

  useEffect(() => {
    // Countdown timer for expiration (30 minutes default)
    if (payment && payment.payment_status === 'pending') {
      const createdAt = new Date(payment.created_at);
      const expiryTime = new Date(createdAt.getTime() + 30 * 60 * 1000); // 30 minutes
      
      const interval = setInterval(() => {
        const now = new Date();
        const diff = expiryTime - now;
        
        if (diff <= 0) {
          setTimeLeft('Expired');
          clearInterval(interval);
        } else {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [payment]);

  const fetchPayment = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pay/${paymentId}`);
      
      if (!response.ok) {
        throw new Error('Payment not found');
      }
      
      const data = await response.json();
      setPayment(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching payment:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied to clipboard!`);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusDisplay = () => {
    if (!payment) return null;
    
    const status = payment.payment_status;
    
    if (['completed', 'finished', 'paid'].includes(status)) {
      return (
        <div className="status-banner success" data-testid="status-success">
          <CheckCircle size={48} />
          <h2>Payment Complete!</h2>
          <p>Thank you for your payment. The merchant has been notified.</p>
        </div>
      );
    }
    
    if (status === 'expired') {
      return (
        <div className="status-banner expired" data-testid="status-expired">
          <AlertCircle size={48} />
          <h2>Payment Expired</h2>
          <p>This payment link has expired. Please contact the merchant for a new link.</p>
        </div>
      );
    }
    
    if (status === 'confirming') {
      return (
        <div className="status-banner confirming" data-testid="status-confirming">
          <Clock size={48} />
          <h2>Confirming Payment</h2>
          <p>We've received your payment and are waiting for blockchain confirmation.</p>
        </div>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="public-payment loading">
        <div className="loading-spinner"></div>
        <p>Loading payment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-payment error-state">
        <AlertCircle size={64} />
        <h2>Payment Not Found</h2>
        <p>{error}</p>
        <p className="hint">Please check the link or contact the merchant.</p>
      </div>
    );
  }

  const isPending = ['pending', 'waiting'].includes(payment?.payment_status);

  return (
    <div className="public-payment" data-testid="public-payment-page">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="payment-header">
        <div className="logo">
          <Bitcoin size={28} />
          <span>Crypto Cashier</span>
        </div>
        {payment?.merchant_business_name && (
          <div className="merchant-name">
            Paying to: <strong>{payment.merchant_business_name}</strong>
          </div>
        )}
        {/* Real-time connection indicator */}
        {isPending && (
          <div className={`realtime-indicator ${wsConnected ? 'connected' : 'disconnected'}`} title={wsConnected ? 'Real-time updates active' : 'Reconnecting...'}>
            {wsConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
            <span>{wsConnected ? 'Live' : 'Updating...'}</span>
          </div>
        )}
      </header>

      {/* Status Banner */}
      {getStatusDisplay()}

      {/* Main Payment Card */}
      {isPending && (
        <div className="payment-card" data-testid="payment-card">
          {/* Amount Display */}
          <div className="amount-section">
            <div className="fiat-amount">
              <span className="currency">{
                ({usd:'$', eur:'€', gbp:'£', cad:'CA$', aud:'AU$', jpy:'¥', chf:'CHF ', inr:'₹'})[payment.price_currency?.toLowerCase()] || '$'
              }</span>
              <span className="value">{payment.price_amount?.toFixed(2)}</span>
              <span className="code">{payment.price_currency?.toUpperCase()}</span>
            </div>
            <ArrowRight className="arrow" />
            <div className="crypto-amount">
              <span className="value">{payment.pay_amount?.toFixed(8)}</span>
              <span className="code">{payment.pay_currency?.toUpperCase()}</span>
            </div>
          </div>

          {/* Timer */}
          {timeLeft && timeLeft !== 'Expired' && (
            <div className="timer-section">
              <Timer size={18} />
              <span>Time remaining: <strong>{timeLeft}</strong></span>
            </div>
          )}

          {/* QR Code */}
          <div className="qr-section">
            <div className="qr-wrapper">
              <QRCodeSVG
                value={payment.pay_address || ''}
                size={220}
                level="H"
                marginSize={4}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
            <p className="qr-hint">Scan with your crypto wallet</p>
          </div>

          {/* Payment Address */}
          <div className="address-section">
            <label>Send {payment.pay_currency?.toUpperCase()} to this address:</label>
            <div 
              className="address-box" 
              onClick={() => copyToClipboard(payment.pay_address, 'Address')}
              data-testid="payment-address"
            >
              <code>{payment.pay_address}</code>
              <button className="copy-btn">
                {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          {/* Amount to Send */}
          <div className="send-amount-section">
            <label>Exact amount to send:</label>
            <div 
              className="send-amount-box" 
              onClick={() => copyToClipboard(payment.pay_amount?.toString(), 'Amount')}
              data-testid="payment-amount"
            >
              <span className="amount">{payment.pay_amount?.toFixed(8)}</span>
              <span className="currency">{payment.pay_currency?.toUpperCase()}</span>
              <button className="copy-btn">
                <Copy size={18} />
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="instructions">
            <h4><Wallet size={18} /> How to pay:</h4>
            <ol>
              <li>Open your crypto wallet app</li>
              <li>Scan the QR code or copy the address</li>
              <li>Send exactly <strong>{payment.pay_amount?.toFixed(8)} {payment.pay_currency?.toUpperCase()}</strong></li>
              <li>Wait for confirmation (usually 10-30 minutes)</li>
            </ol>
          </div>

          {/* External Payment Link */}
          {payment.payment_url && (
            <a 
              href={payment.payment_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="external-pay-btn"
              data-testid="external-pay-btn"
            >
              <ExternalLink size={18} />
              Pay via NOWPayments
            </a>
          )}
        </div>
      )}

      {/* Description */}
      {payment?.description && (
        <div className="description-section">
          <p><strong>Description:</strong> {payment.description}</p>
        </div>
      )}

      {/* Footer */}
      <footer className="payment-footer">
        <p>Powered by <strong>Crypto Cashier</strong></p>
        <p className="secure">🔒 Secure cryptocurrency payment</p>
      </footer>
    </div>
  );
};

export default PublicPayment;
